import { Component, OnInit } from '@angular/core';
import { UserService } from '../../../../../services/user.service';
import { SuggestionListsService } from '../../services/suggestion-lists.service';
import { ComponentBackgroundService } from '../../../../../services/component-background.service';
import { SortTableService } from '../../../../../services/sort-table.service';
import { User } from '../../../../../models/user.model';
import { AppConstants } from '../../../../views/view-user-profile/app.constants';

@Component({
  selector: 'app-all-suggestion-lists',
  templateUrl: './all-suggestion-lists.component.html',
  styleUrls: ['./all-suggestion-lists.component.css']
})
export class AllSuggestionListsComponent implements OnInit {
  isLoaded = false;
  allSLList: any[] = [];
  allSLListClonedForSearching: any[] = [];
  cancel_associated_session: any = false;
  internalServerError = false;
  getGroupError = false;
  getSelfError = false;
  thisUser: any;
  user: User;
  isAdmin = false;
  isUser = false;
  isHead = false;
  
  currentPage = 0;
  pageSize = 10;
  totalPages = 0;
  totalElements = 0;
  
  isLoadingNextPage = false;
  
  private currentDisplayMode: 'all' | 'review' = 'all';


  constructor(public suggestionListsService: SuggestionListsService,
    public componentBackgroundService: ComponentBackgroundService,
    private userService: UserService,
    public sortTableService: SortTableService,
) {

  }

  checkRole() {
    this.user = this.userService.getLocal();
    
    this.isHead = false;
    this.isAdmin = false;
    this.isUser = false;
    
    this.user.roles.forEach(role => {
      switch(role) {
        case 'ROLE_HEAD':
          this.isHead = true;
          break;
        case 'ROLE_ADMIN':
          this.isAdmin = true;
          break;
        case 'ROLE_USER':
          this.isUser = true;
          break;
      }
    });
  }

  getList(page: number = 0){
    this.currentDisplayMode = 'all';
    this.prepareForLoading(page);
    
    this.userService.getSelf().subscribe(
      (res: any) => this.handleUserResponseForList(res, page),
      () => this.handleUserError()
    );
  }

  private handleUserError() {
    this.internalServerError = true;
    this.getGroupError = false;
    this.getSelfError = true;
    this.finishLoading(false);
  }

  private prepareForLoading(page: number) {
    this.isLoadingNextPage = true;
    this.currentPage = page;
  }

  private handleUserResponseForList(res: any, page: number) {
    if (res.status === AppConstants.HTTP_STATUS.OK) {
      this.thisUser = res.body;
      this.loadAllSuggestions(page);
    } else {
      this.setSelfError();
    }
  }

  private setSelfError() {
    this.internalServerError = false;
    this.getGroupError = false;
    this.getSelfError = true;
    this.finishLoading(false);
  }

  private loadAllSuggestions(page: number) {
    this.suggestionListsService.getAllSLByUserIDWithPagination(this.thisUser.mainGroup, page, this.pageSize).subscribe(
      (response: any) => this.handleAllSuggestionsResponse(response, page),
      (error) => this.handleAllSuggestionsError(error)
    );
  }

  private handleAllSuggestionsResponse(response: any, page: number) {
    if (response.status === AppConstants.HTTP_STATUS.OK) {
      this.processSuccessfulResponse(response.body, page, 'allSuggestionListSort');
    } else {
      this.setGroupError();
    }
  }

  private processSuccessfulResponse(pageResponse: any, page: number, sortKey: string) {
    const newLists = (pageResponse && pageResponse.suggestions) ? pageResponse.suggestions : [];
    this.allSLList = newLists;
    
    this.updatePaginationData(pageResponse, page);
    this.finalizeSuggestionsList(sortKey);
  }

  private updatePaginationData(pageResponse: any, page: number) {
    this.currentPage = (pageResponse && pageResponse.currentPage !== undefined) ? pageResponse.currentPage : page;
    this.totalPages = (pageResponse && pageResponse.totalPages !== undefined) ? pageResponse.totalPages : 1;
    this.totalElements = (pageResponse && pageResponse.totalElements !== undefined) ? pageResponse.totalElements : 0;
  }

  private finalizeSuggestionsList(sortKey: string) {
    this.sortTableService.sortList(sortKey, this.allSLList, 'department');
    this.allSLListClonedForSearching = [...this.allSLList];
    this.finishLoading(false);
  }

  private handleAllSuggestionsError(error: any) {
    console.error('Error loading all suggestions:', error);
    if (error.status === AppConstants.HTTP_STATUS.UNPROCESSIBLE) {
      this.setGroupError();
    } else {
      this.setGroupErrorWithServerError();
    }
  }

  private setGroupError() {
    this.internalServerError = false;
    this.getGroupError = true;
    this.getSelfError = false;
    this.finishLoading(false);
  }

  private finishLoading(hasError: boolean) {
    this.isLoaded = true;
    this.isLoadingNextPage = false;
    if (hasError) {
      this.internalServerError = true;
    }
  }

  private setGroupErrorWithServerError() {
    this.internalServerError = true;
    this.getGroupError = true;
    this.getSelfError = false;
    this.finishLoading(false);
  }

  ngOnInit() {
    this.checkRole();
    this.resetErrorState();
    this.getList(0);
  }

  private resetErrorState() {
    this.isLoaded = false;
    this.internalServerError = false;
    this.getGroupError = false;
    this.getSelfError = false;
  }

  getSLForReview(page: number = 0) {
    this.currentDisplayMode = 'review';
    this.prepareForLoading(page);
    
    this.suggestionListsService.getSLForReviewByHeadIDWithPagination(this.thisUser.id, page, this.pageSize).subscribe(
      (response: any) => this.handleReviewResponse(response, page),
      (error) => this.handleReviewError(error)
    );
  }

  private handleReviewResponse(response: any, page: number) {
    if (response.status === AppConstants.HTTP_STATUS.OK) {
      this.processSuccessfulResponse(response.body, page, 'allReviewSLList');
    } else {
      this.setGroupError();
    }
  }

  private handleReviewError(error: any) {
    console.error('Error loading review suggestions:', error);
    this.setGroupErrorWithServerError();
  }

  openSLDetails(list) {
    window.open('/dashboard/suggestion-list-details/' + list.id, '_blank');
  }

  searchText(value) {
    if (value) {
      this.filterByDescription(value);
    } else {
      this.resetSearch();
    }
  }

  private filterByDescription(value: string) {
    const regexp = new RegExp(value, 'i');
    this.allSLListClonedForSearching = [...this.allSLList].filter(elem => regexp.test(elem.description));
  }

  private resetSearch() {
    this.allSLListClonedForSearching = [...this.allSLList];
  }
  
  loadNextPage() {
    if (!this.isLoadingNextPage && this.hasNextPage()) {
      this.loadPageByMode(this.currentPage + 1);
    }
  }

  loadPreviousPage() {
    if (!this.isLoadingNextPage && this.currentPage > 0) {
      this.loadPageByMode(this.currentPage - 1);
    }
  }

  loadNext5Pages() {
    if (!this.isLoadingNextPage) {
      const targetPage = Math.min(this.currentPage + 5, this.totalPages - 1);
      if (targetPage > this.currentPage) {
        this.loadPageByMode(targetPage);
      }
    }
  }

  loadPrevious5Pages() {
    if (!this.isLoadingNextPage) {
      const targetPage = Math.max(this.currentPage - 5, 0);
      if (targetPage < this.currentPage) {
        this.loadPageByMode(targetPage);
      }
    }
  }

  private loadPageByMode(page: number) {
    if (this.currentDisplayMode === 'review') {
      this.getSLForReview(page);
    } else {
      this.getList(page);
    }
  }

  hasNextPage(): boolean {
    return this.currentPage < (this.totalPages - 1);
  }

  hasPreviousPage(): boolean {
    return this.currentPage > 0;
  }

  hasNext5Pages(): boolean {
    return this.currentPage + 5 < this.totalPages;
  }

  hasPrevious5Pages(): boolean {
    return this.currentPage >= 5;
  }

}


// WEBPACK FOOTER //
// C:/work/new2/internal-frontend/src/app/content-page/common/suggestion-lists/components/all-suggestion-lists/all-suggestion-lists.component.ts